import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  DispositionAddForm,
  DispositionAddInitialValues,
  DispositionComponents,
  DispositionAdditionalParams,
  DispositionFormOptions,
  onDispositionFormSaved,
  IDispositionFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateDisposition, PermissionEnum, } from 'atlas-shared';
import { createDisposition, fetchDisposition, getDispositionCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useForms, useDispositions, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateDisposition> {}

const DispositionFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const forms = useForms();
  const dispositions = useDispositions();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateDisposition>>();

  const param_props: IDispositionFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    forms: forms.forms,
    dispositions: dispositions.dispositions,
  };
  const formLayout = useMemo(() => DispositionAddForm(values, param_props, t), [values, organizations, forms, dispositions, t, abilities, ]);
  const options: IFormOptions = useMemo(() => DispositionFormOptions(values || DispositionAddInitialValues, param_props, t), [values, organizations, forms, dispositions, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateDisposition, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateDisposition) => save(() => createDisposition(values).then(
    res => onSaved ? onSaved(res, navigate) : onDispositionFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchDisposition(+id)
        .then(({
          organization_id,
          title,
          conversation_form_id,
          parents,
          global,
        }) => {
          setCloned({
            organization_id,
            title,
            conversation_form_id,
            parents,
            global,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !forms.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.Disposition, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={DispositionComponents}
        additionalParams={DispositionAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...DispositionAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('DISPOSITION')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const DispositionFormAddView = React.memo(withAdd(DispositionFormAdd, getDispositionCreateSchema, DispositionAddInitialValues));
